import {
  getBackendAvailabilitySnapshot,
  getBackendProbeSuccessRevision,
  markBackendAvailable,
  markBackendUnavailable,
  probeBackendAvailabilityNow,
} from '../../../utils/backend-availability';

type GateOutcome = 'available' | 'retry' | 'unavailable';

interface PendingVerification {
  promise: Promise<GateOutcome>;
  resolve: (outcome: GateOutcome) => void;
  token: symbol;
}

interface BackendRequestGateState {
  blockedAtProbeRevision: number | null;
  pending: PendingVerification | null;
  verified: boolean;
}

export interface BackendRequestPermit {
  readonly leaderToken: symbol | null;
  readonly scope: string;
}

/**
 * Single-flight transport verification shared by backend API clients.
 * One request proves reachability after startup or recovery while concurrent
 * bootstrap requests wait locally.
 */
export class BackendRequestGate {
  private readonly states = new Map<string, BackendRequestGateState>();

  constructor(
    private readonly canAttemptRequest = getBackendAvailabilitySnapshot,
    private readonly getProbeSuccessRevision = getBackendProbeSuccessRevision,
    private readonly verifyAvailability?: () => Promise<boolean>
  ) {}

  async acquire(
    scope: string,
    signal?: AbortSignal
  ): Promise<BackendRequestPermit | null> {
    if (signal?.aborted) throw signal.reason;
    const state = this.stateFor(scope);
    if (state.blockedAtProbeRevision !== null) {
      if (this.getProbeSuccessRevision() <= state.blockedAtProbeRevision) {
        return null;
      }
      state.blockedAtProbeRevision = null;
      state.verified = true;
    }
    if (!this.canAttemptRequest()) return null;
    if (state.verified) return { scope, leaderToken: null };

    if (state.pending) {
      const outcome = await raceWithSignal(state.pending.promise, signal);
      if (outcome === 'retry') return this.acquire(scope, signal);
      return outcome === 'available' ? { scope, leaderToken: null } : null;
    }

    if (this.verifyAvailability) {
      const pending = this.createPendingVerification(state);
      void this.verifyAvailability().then(
        available => {
          if (available) this.markAvailable(scope);
          else this.markUnavailable(scope);
        },
        () => this.markUnavailable(scope)
      );
      const outcome = await raceWithSignal(pending.promise, signal);
      return outcome === 'available' ? { scope, leaderToken: null } : null;
    }

    const pending = this.createPendingVerification(state);
    return { scope, leaderToken: pending.token };
  }

  markAvailable(scope: string): void {
    const state = this.stateFor(scope);
    state.blockedAtProbeRevision = null;
    state.verified = true;
    this.settlePending(state, 'available');
  }

  markUnavailable(scope: string): void {
    const state = this.stateFor(scope);
    state.blockedAtProbeRevision = this.getProbeSuccessRevision();
    state.verified = false;
    this.settlePending(state, 'unavailable');
  }

  releaseUnverified(permit: BackendRequestPermit): void {
    if (!permit.leaderToken) return;
    const state = this.stateFor(permit.scope);
    if (state.pending?.token !== permit.leaderToken) return;
    state.pending.resolve('retry');
    state.pending = null;
  }

  resetForTests(): void {
    for (const state of this.states.values()) state.pending?.resolve('retry');
    this.states.clear();
  }

  private stateFor(scope: string): BackendRequestGateState {
    let state = this.states.get(scope);
    if (!state) {
      state = {
        blockedAtProbeRevision: null,
        pending: null,
        verified: false,
      };
      this.states.set(scope, state);
    }
    return state;
  }

  private settlePending(
    state: BackendRequestGateState,
    outcome: GateOutcome
  ): void {
    state.pending?.resolve(outcome);
    state.pending = null;
  }

  private createPendingVerification(
    state: BackendRequestGateState
  ): PendingVerification {
    let resolve!: (outcome: GateOutcome) => void;
    const token = Symbol('backend-request-verification');
    const promise = new Promise<GateOutcome>(resolvePromise => {
      resolve = resolvePromise;
    });
    const pending = { promise, resolve, token };
    state.pending = pending;
    return pending;
  }
}

export const defaultBackendRequestGate = new BackendRequestGate(
  getBackendAvailabilitySnapshot,
  getBackendProbeSuccessRevision,
  probeBackendAvailabilityNow
);

export class BackendKnownUnavailableError extends Error {
  constructor() {
    super('Backend is known unavailable');
    this.name = 'BackendKnownUnavailableError';
  }
}

export async function gatedBackendFetch(
  input: string | URL,
  init: RequestInit | undefined,
  options: {
    baseUrl?: string;
    fetchImpl?: typeof globalThis.fetch;
    gate?: BackendRequestGate;
    isCancellation?: (error: unknown) => boolean;
  } = {}
): Promise<Response> {
  const gate = options.gate ?? defaultBackendRequestGate;
  const url = new URL(input, options.baseUrl);
  const permit = await gate.acquire(url.origin, init?.signal ?? undefined);
  if (!permit) throw new BackendKnownUnavailableError();

  try {
    const response = await (options.fetchImpl ?? globalThis.fetch)(
      url.href,
      init
    );
    // Every HTTP response proves transport reachability. Application-level
    // 5xx responses can be scoped to one workspace, shard or dependency and
    // must never trip the origin-wide transport circuit.
    gate.markAvailable(url.origin);
    markBackendAvailable();
    return response;
  } catch (error) {
    if (options.isCancellation?.(error)) {
      gate.releaseUnverified(permit);
    } else {
      gate.markUnavailable(url.origin);
      markBackendUnavailable(error);
    }
    throw error;
  }
}

export function isExplicitBackendRequestCancellation(
  error: unknown,
  signal?: AbortSignal | null
): boolean {
  if (!signal?.aborted) return false;
  return (
    error === signal.reason ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

function raceWithSignal<T>(
  promise: Promise<T>,
  signal?: AbortSignal
): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(signal.reason);

  return new Promise<T>((resolve, reject) => {
    const onAbort = () => {
      signal.removeEventListener('abort', onAbort);
      reject(signal.reason);
    };
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      value => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      error => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      }
    );
  });
}
